import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import PublicLayout from "@/components/layout/PublicLayout";
import SectionEyebrow from "@/components/common/SectionEyebrow";
import StatusBadge from "@/components/common/StatusBadge";
import Loader from "@/components/common/Loader";
import { publicApi } from "@/lib/api";
import { ArrowRight, ArrowUpRight, BookOpen } from "lucide-react";

const LABELS = {
  amazon_us: "Amazon US",
  amazon_india: "Amazon India",
  inkscribe: "InkScribe Store",
  flipkart: "Flipkart",
  kindle: "Kindle",
  google_books: "Google Books",
  amazon_prime: "Amazon Prime",
};

export default function BookDetail() {
  const { slug } = useParams();
  const [book, setBook] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    publicApi.book(slug).then(setBook).catch(() => setMissing(true));
  }, [slug]);

  if (missing) {
    return (
      <PublicLayout title="Book not found">
        <section className="py-32 text-center">
          <h1 className="font-serif text-4xl">Book not found</h1>
          <Link to="/beyond-science" className="mt-6 inline-flex items-center bsca-link">Back to series</Link>
        </section>
      </PublicLayout>
    );
  }
  if (!book) return <PublicLayout><Loader label="Loading book" /></PublicLayout>;

  return (
    <PublicLayout title={book.title}>
      <section className="relative bsca-dark bsca-cosmic bsca-grain -mt-16 md:-mt-20 lg:-mt-[88px] pt-32 md:pt-40 pb-24 overflow-hidden">
        <div className="bsca-field-lines" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-14 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-5">
            <div className="aspect-[3/4] relative bg-[#0A1120] overflow-hidden shadow-2xl">
              <img src={book.cover_image} alt={book.title} className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute top-4 left-4">
                <StatusBadge status={book.status} label={book.status_label} />
              </div>
            </div>
          </div>
          <div className="md:col-span-7 bsca-fade-up">
            <div className="bsca-eyebrow text-[#D4AF37]">Volume {book.volume} · {book.series_title || "Beyond Science"}</div>
            <h1 className="mt-3 font-serif text-white text-4xl md:text-6xl tracking-tight leading-[1.02]">{book.title}</h1>
            <div className="mt-3 text-white/70 italic text-lg">{book.subtitle}</div>
            <div className="mt-8 border-l-2 border-[#D4AF37] pl-5">
              <div className="bsca-eyebrow text-[#D4AF37] text-[10px]">Core Question</div>
              <p className="mt-1 font-serif text-2xl text-white">{book.core_question}</p>
            </div>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/engage#join" data-testid="btn-book-join" className="bsca-lift inline-flex items-center bg-[#D4AF37] text-[#050B14] font-medium px-7 py-3.5 rounded-sm">Join the Alliance <ArrowRight className="h-4 w-4 ml-2" /></Link>
              <Link to="/engage#contribution" data-testid="btn-book-contribute" className="bsca-lift inline-flex items-center border border-white/30 text-white px-7 py-3.5 rounded-sm hover:border-[#D4AF37]/60">Submit a Reflection</Link>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#F8F6F1] py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-7 space-y-8">
            <div>
              <SectionEyebrow>About this volume</SectionEyebrow>
              <p className="text-lg leading-relaxed text-[#1f2937]">{book.description}</p>
            </div>
            <div>
              <SectionEyebrow>Chapter Highlights</SectionEyebrow>
              <ul className="space-y-3">
                  {(book.chapter_highlights || []).map((c, i) => (
                    <li key={c} className="flex items-start gap-4">
                      <span className="bsca-eyebrow text-[#7A5A06] mt-1.5">0{i + 1}</span>
                      <span className="text-[#1f2937] leading-relaxed">{c}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="md:col-span-5">
            <div className="bg-white border border-black/10 p-8 sticky top-28">
              <BookOpen className="h-6 w-6 text-[#D4AF37]" strokeWidth={1.4} />
              <h3 className="mt-4 font-serif text-2xl">Where to read</h3>
              <p className="mt-2 text-sm text-[#4B5563]">Retail links available for this volume. Empty links are CMS placeholders.</p>
              <div className="mt-6 space-y-3">
                {Object.entries(book.retail_links || {}).map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between border-b border-black/10 py-3">
                    <span className="text-sm">{LABELS[k] || k}</span>
                    {v ? (
                      <a href={v} target="_blank" rel="noopener noreferrer" data-testid={`book-retail-${k}`} className="bsca-link text-sm inline-flex items-center text-[#050B14]">
                        Open <ArrowUpRight className="h-3 w-3 ml-1" />
                      </a>
                    ) : (
                      <span className="text-xs bsca-eyebrow text-[#9CA3AF]">Link pending</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
